import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSave, FiSearch, FiGlobe } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite, useAdminAccess } from '../utilities/adminAccess';
import { extractList, isEnabled } from '../utilities/tournamentsApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import { FlagImg } from '../components/FlagSelect';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';

export default function TournamentsManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canReadChamp } = useAdminAccess('tournaments');

  const [countries, setCountries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!champId && champsLoading) return;
    if (!champId || !canReadChamp) {
      setCountries([]);
      setOriginalOrder([]);
      setLoading(false);
      setMessage({
        text: champsLoading ? '' : t(!champId ? 'championshipsLoadError' : 'champNoAccess'),
        type: champsLoading ? '' : 'error',
      });
      return;
    }
    loadCountries();
  }, [champId, champsLoading, canReadChamp]);

  const filteredCountries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((item) => String(item.name || '').toLowerCase().includes(q));
  }, [countries, searchQuery]);

  const loadCountries = async () => {
    if (!champId) {
      setCountries([]);
      setOriginalOrder([]);
      setLoading(false);
      setMessage({ text: t('championshipsLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountries, { champ_id: champId });
      const list = extractList(response.data);
      setCountries(list);
      setOriginalOrder(list);
      setReorderMode(false);
    } catch (error) {
      setCountries([]);
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.changeCountriesPriority, {
        countries: countries.map((item, index) => ({ id: item.id, sortIdx: index })),
      });
      const ordered = countries.map((item, index) => ({ ...item, sortIdx: index }));
      setCountries(ordered);
      setOriginalOrder(ordered);
      setReorderMode(false);
      setMessage({ text: t('stOrderSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stOrderError')), type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  const columns = [
    {
      key: 'show',
      label: t('stStatus'),
      className: 'st-cell-status',
      render: (item) => (
        <span className={`st-badge ${isEnabled(item.show) ? 'enabled' : 'disabled'}`}>
          <span className="tr-btn-full">{isEnabled(item.show) ? t('stEnabled') : t('stDisabled')}</span>
          <span className="tr-btn-short">{isEnabled(item.show) ? t('stEnabledShort') : t('stDisabledShort')}</span>
        </span>
      ),
    },
    {
      key: 'name',
      label: t('stdName'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="tr-name">
          <FlagImg code={item.flag} className="tr-flag-img" />
          <span>{item.name || '—'}</span>
        </span>
      ),
    },
    {
      key: 'sortIdx',
      label: t('stdPriority'),
      className: 'st-cell-priority',
      render: (item, index) => (
        <span className="st-priority-badge">{reorderMode ? index + 1 : (item.sortIdx ?? index + 1)}</span>
      ),
    },
  ];

  const visibleItems = reorderMode ? countries : filteredCountries;

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tr-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navTournaments') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('trSearchPh')}
              value={searchQuery}
              disabled={reorderMode}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          <div className="st-meta">
            <strong>{t('stFound', { count: visibleItems.length })}</strong>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content">
            <div className="st-block">
              <h2 className="st-block-header">{t('trChampionships')}</h2>
              {loading ? (
                <div className="st-loading">
                  <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                  <p>{t('trLoading')}</p>
                </div>
              ) : (
                <ReorderableTable
                  tableClassName="st-table-three"
                  columns={columns}
                  items={visibleItems}
                  getRowKey={(item) => item.id}
                  reorderMode={reorderMode}
                  onItemsChange={setCountries}
                  getRowHref={(item) => (item.id ? `/tournaments/${item.id}` : null)}
                  empty={(
                    <div className="st-empty">
                      <FiGlobe size={48} />
                      <h3>{t('trEmptyCountries')}</h3>
                      <p>{t('trEmptyCountriesHint')}</p>
                    </div>
                  )}
                />
              )}
              {reorderMode ? <p className="st-hint">{t('stDragHint')}</p> : null}
            </div>
          </div>
        </div>

        <div className="st-actions">
          {reorderMode ? (
            <>
              <button
                type="button"
                className="st-btn st-btn-secondary"
                disabled={savingOrder}
                onClick={() => {
                  setCountries(originalOrder);
                  setReorderMode(false);
                }}
              >
                {t('stCancelReorder')}
              </button>
              <button
                type="button"
                className="st-btn st-btn-primary"
                disabled={savingOrder || !countries.length}
                onClick={handleSaveOrder}
              >
                <FiSave /> {savingOrder ? t('trLoading') : t('stSaveOrder')}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/tournaments/applications"
                className={`st-btn st-btn-secondary${!canReadChamp ? ' is-disabled' : ''}`}
                aria-disabled={!canReadChamp || undefined}
                onClick={(event) => { if (!canReadChamp) event.preventDefault(); }}
              >
                {t('trManageApplications')}
              </Link>
              <IfCanWrite entity="tournaments">
                <button
                  type="button"
                  className="st-btn st-btn-secondary"
                  disabled={loading || countries.length < 2 || Boolean(searchQuery.trim())}
                  onClick={() => {
                    setOriginalOrder(countries);
                    setReorderMode(true);
                    setMessage({ text: '', type: '' });
                  }}
                >
                  {t('stReorder')}
                </button>
                <Link to="/tournaments/new" className="st-btn st-btn-primary">
                  <FiPlus /> {t('trAddCountry')}
                </Link>
              </IfCanWrite>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
